/**
 * Runtime routing of an MBR procedure graph (PROJECT_SPEC.md §6.1 conditional/branching logic, parallel
 * branches with join semantics; WF-003, WF-005, WF-006, WF-007; 21 CFR 11.10(f) operational system checks).
 *
 * Pure: the server loads the step states and recorded values and asks `routeFacts` which steps may start.
 * Anything it cannot decide (a DECISION whose inputs are not recorded yet) is "pending", never "open" —
 * it fails closed. Back-edges (LOOP bodies) are ignored for readiness so a loop's first pass is not deadlocked.
 */
import { dec } from "../calc/decimal.js";
import { evaluateFormula } from "../calc/formula.js";
import type { MbrContent } from "./content.js";

type Transition = MbrContent["procedure"]["transitions"][number];
const DONE = new Set(["COMPLETE", "SKIPPED"]);
/** The one condition shape the runtime evaluates: `<expr> <op> <expr>` over recorded decimals. */
export const CONDITION_SHAPE = /^(.+?)\s*(<=|>=|==|!=|<|>|=)\s*(.+)$/;

export interface RoutingFacts {
  prereqStepsComplete: boolean;
  parallelBranchesJoined: boolean;
  /** true when the step sits on a DECISION branch that was not taken (WF-006): it can never run. */
  notTaken: boolean;
  reason: string;
}

/** `LHS op RHS` over recorded decimal values; null = cannot be decided yet (unknown input / bad syntax). */
export function evaluateCondition(expr: string, values: Readonly<Record<string, string>>): boolean | null {
  const m = CONDITION_SHAPE.exec(expr);
  if (!m) return null;
  const side = (s: string) => evaluateFormula(s, { ...values }, { precision: 9, rounding: "HALF_UP" });
  const a = side(m[1] as string);
  const b = side(m[3] as string);
  if (!a.ok || !b.ok) return null;
  const x = dec(a.value);
  const y = dec(b.value);
  switch (m[2]) {
    case "<":
      return x.lt(y);
    case "<=":
      return x.lte(y);
    case ">":
      return x.gt(y);
    case ">=":
      return x.gte(y);
    case "!=":
      return !x.eq(y);
    default:
      return x.eq(y);
  }
}

/** The edge a DECISION takes: first true conditional edge in document order, else the unconditional default. */
export function chosenEdge(
  out: readonly Transition[],
  values: Readonly<Record<string, string>>,
): Transition | undefined | "UNDECIDABLE" {
  for (const t of out) {
    if (t.condition === undefined) continue;
    const r = evaluateCondition(t.condition, values);
    if (r === null) return "UNDECIDABLE";
    if (r) return t;
  }
  return out.find((t) => t.condition === undefined);
}

function forwardEdges(c: MbrContent): Transition[] {
  const out = new Map<string, Transition[]>();
  for (const t of c.procedure.transitions) out.set(t.from, [...(out.get(t.from) ?? []), t]);
  const hasIn = new Set(c.procedure.transitions.map((t) => t.to));
  const roots = c.procedure.steps.filter((s) => !hasIn.has(s.id)).map((s) => s.id);
  const state = new Map<string, 1 | 2>();
  const back = new Set<Transition>();
  const visit = (id: string): void => {
    state.set(id, 1);
    for (const t of out.get(id) ?? []) {
      if (state.get(t.to) === 1) back.add(t);
      else if (!state.has(t.to)) visit(t.to);
    }
    state.set(id, 2);
  };
  for (const r of roots.length ? roots : [c.procedure.steps[0]?.id ?? ""]) if (!state.has(r)) visit(r);
  return c.procedure.transitions.filter((t) => !back.has(t));
}

type EdgeStatus = "LIVE" | "DEAD" | "PENDING";

function joinOk(
  join: string | undefined,
  joinCount: number | undefined,
  live: number,
  blocking: number,
): boolean {
  if (join === "ALL") return live >= 1 && blocking === 0;
  if (join === "ANY") return live >= 1;
  if (join === "N_OF_M") return live >= (joinCount ?? 1);
  return true;
}

/**
 * @param states   step id → state of its latest execution (absent = never started)
 * @param values   parameter code → current recorded decimal string
 * @param pinned   decision step id → the step its taken branch leads to, fixed when the first step on that
 *                 branch started (WF-006). A pinned decision is never re-evaluated from `values`, so a later
 *                 correction cannot move a branch that is already under way (21 CFR 211.188(b)).
 */
export function routeFacts(
  content: MbrContent,
  states: Readonly<Record<string, string>>,
  values: Readonly<Record<string, string>>,
  pinned: Readonly<Record<string, string>> = {},
): Map<string, RoutingFacts> {
  const edges = forwardEdges(content);
  const byId = new Map(content.procedure.steps.map((s) => [s.id, s]));
  const incoming = (id: string) => edges.filter((t) => t.to === id);
  const memo = new Map<Transition, EdgeStatus>();

  const decisionEdge = (t: Transition): EdgeStatus => {
    const fixed = pinned[t.from];
    if (fixed !== undefined) return fixed === t.to ? "LIVE" : "DEAD";
    const pick = chosenEdge(
      content.procedure.transitions.filter((x) => x.from === t.from),
      values,
    );
    return pick === "UNDECIDABLE" ? "PENDING" : pick === t ? "LIVE" : "DEAD";
  };
  const edgeStatus = (t: Transition): EdgeStatus => {
    const hit = memo.get(t);
    if (hit) return hit;
    let r: EdgeStatus = "PENDING";
    if (isDead(t.from)) r = "DEAD";
    else if (DONE.has(states[t.from] ?? ""))
      r = byId.get(t.from)?.type === "DECISION" ? decisionEdge(t) : "LIVE";
    memo.set(t, r);
    return r;
  };
  const isDead = (id: string): boolean => {
    const inc = incoming(id);
    return inc.length > 0 && inc.every((t) => edgeStatus(t) === "DEAD");
  };

  const result = new Map<string, RoutingFacts>();
  for (const s of content.procedure.steps) {
    const inc = incoming(s.id);
    const st = inc.map((t) => ({ t, s: edgeStatus(t) }));
    const live = st.filter((e) => e.s === "LIVE").length;
    const pending = st.filter((e) => e.s === "PENDING").map((e) => e.t);
    // an optional branch never holds a join open (WF-005 speaks of MANDATORY branches)
    const blocking = pending.filter((t) => byId.get(t.from)?.mandatory !== false);
    const notTaken = isDead(s.id);
    const prereq = inc.length === 0 || (!notTaken && live >= 1 && (s.join ? true : pending.length === 0));
    const joined = joinOk(s.join, s.joinCount, live, blocking.length);
    const reason = notTaken
      ? "this step is on a branch the recipe did not take"
      : !prereq
        ? `waiting for ${pending.map((t) => t.from).join(", ") || "a decision"}`
        : joined
          ? ""
          : `parallel branches still open: ${blocking.map((t) => t.from).join(", ")}`;
    result.set(s.id, { prereqStepsComplete: prereq, parallelBranchesJoined: joined, notTaken, reason });
  }
  return result;
}

/**
 * The decision branches that step `stepId` is the first to start on: every forward edge into it that leaves
 * a completed DECISION and is the edge the decision takes over `values`. The caller pins these (WF-006) in
 * the same transaction that starts the step.
 */
export function branchesToPin(
  content: MbrContent,
  stepId: string,
  values: Readonly<Record<string, string>>,
): { decisionStepId: string; toStepId: string }[] {
  const decisions = new Set(content.procedure.steps.filter((s) => s.type === "DECISION").map((s) => s.id));
  const out: { decisionStepId: string; toStepId: string }[] = [];
  for (const t of forwardEdges(content)) {
    if (t.to !== stepId || !decisions.has(t.from)) continue;
    const pick = chosenEdge(
      content.procedure.transitions.filter((x) => x.from === t.from),
      values,
    );
    if (pick === t) out.push({ decisionStepId: t.from, toStepId: t.to });
  }
  return out;
}

/**
 * Would recomputing the pinned decisions over `values` send any of them down a different branch? Used to
 * refuse a correction that would re-route a branch already under way (WF-006): returns the decisions that
 * would flip, empty when the correction is safe.
 */
export function flippedPins(
  content: MbrContent,
  pinned: Readonly<Record<string, string>>,
  values: Readonly<Record<string, string>>,
): string[] {
  return Object.entries(pinned)
    .filter(([decisionId, toStepId]) => {
      const pick = chosenEdge(
        content.procedure.transitions.filter((x) => x.from === decisionId),
        values,
      );
      return pick === "UNDECIDABLE" || pick?.to !== toStepId;
    })
    .map(([decisionId]) => decisionId);
}
